'use client';

import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Service {
  id: string;
  name: string;
  description: string;
  duration: string;
  startingPrice: string;
  imageUrl: string;
  imageAlt: string;
  features: string[];
  iconName: 'HomeIcon' | 'BuildingOfficeIcon' | 'TruckIcon' | 'SparklesIcon';
}

const services: Service[] = [
  {
    id: 'service_deep',
    name: 'Deep Clean',
    description: 'Comprehensive top-to-bottom cleaning for a thorough refresh',
    duration: '4-6 hours',
    startingPrice: '$349',
    imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1e318165c-1770383106459.png",
    imageAlt: 'Professional cleaner deep cleaning kitchen appliances with eco-friendly products',
    features: ['All surfaces sanitized', 'Inside appliances', 'Baseboards & trim', 'Window sills'],
    iconName: 'SparklesIcon'
  },
  {
    id: 'service_standard',
    name: 'Standard Clean',
    description: 'Regular maintenance cleaning to keep your home fresh',
    duration: '2-3 hours',
    startingPrice: '$199',
    imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1f07ce75f-1768486939794.png",
    imageAlt: 'Cleaner vacuuming living room carpet with modern vacuum',
    features: ['Dusting & vacuuming', 'Kitchen & bathroom', 'Trash removal', 'Surface wipe-down'],
    iconName: 'HomeIcon'
  },
  {
    id: 'service_moveinout',
    name: 'Move-In/Out',
    description: 'Complete cleaning for property transitions',
    duration: '5-8 hours',
    startingPrice: '$469',
    imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1d34345f0-1766936034121.png",
    imageAlt: 'Empty room being cleaned with mop and bucket, windows sparkling',
    features: ['Empty property cleaning', 'All rooms & closets', 'Appliance interior', 'Wall spot cleaning'],
    iconName: 'TruckIcon'
  },
  {
    id: 'service_office',
    name: 'Office Cleaning',
    description: 'Professional workspace maintenance for businesses',
    duration: 'Custom schedule',
    startingPrice: '$249',
    imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1d7e7871f-1766936034810.png",
    imageAlt: 'Modern office space with clean desks and organized workspace',
    features: ['Desk & surface cleaning', 'Floor maintenance', 'Restroom sanitizing', 'Trash & recycling'],
    iconName: 'BuildingOfficeIcon'
  }
];


export default function ServicesSection() {
  const router = useRouter();

  const handleBookService = (serviceKey: string) => {
    router.push(`/homepage#booking?service=${serviceKey}`);
  };

  const handleContactUs = () => {
    router.push('/contact');
  };

  return (
    <section id="services" className="w-full bg-background py-16 md:py-20 lg:py-32 xl:py-40 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24 max-w-screen-2xl">
        
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-12 lg:mb-16 xl:mb-20 reveal">
          <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground tracking-tight mb-4">
            Our Cleaning Services
          </h2>
          <p className="text-base md:text-lg xl:text-xl text-muted-foreground max-w-2xl xl:max-w-3xl mx-auto">
            Professional cleaning solutions tailored to your needs. All services include eco-friendly products and satisfaction guarantee.
          </p>
        </div>
        
        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-5 lg:gap-6 xl:gap-8">
          
          {/* Large Card - Deep Clean (spans 2 columns on lg) */}
          <div className="md:col-span-2 lg:col-span-2 bg-card border border-border rounded-xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 hover:border-[#4CAF50] transition-all duration-300 group reveal">
            <div className="relative h-48 sm:h-56 md:h-60 lg:h-80 overflow-hidden">
              <AppImage
                src={services[0].imageUrl}
                alt={services[0].imageAlt}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 50vw"
                quality={80}
                className="object-cover group-hover:scale-105 transition-transform duration-500" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6">
                <div className="flex items-center gap-3 mb-2 sm:mb-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-full flex items-center justify-center">
                    <Icon name={services[0].iconName} size={20} variant="solid" className="text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white">{services[0].name}</h3>
                    <p className="text-xs sm:text-sm text-white/80">{services[0].duration}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 md:p-5 sm:p-6">
              <p className="text-muted-foreground mb-3 sm:mb-4 text-sm sm:text-base">{services[0].description}</p>
              <ul className="space-y-2 mb-4 sm:mb-6">
                {services[0].features.map((feature, idx) =>
                <li key={`${services[0].id}_feature_${idx}`} className="flex items-center gap-2 text-sm text-foreground">
                    <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-primary flex-shrink-0" />
                    {feature}
                  </li>
                )}
              </ul>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs sm:text-sm text-muted-foreground">Starting at</span>
                  <p className="text-2xl sm:text-3xl font-bold text-primary">{services[0].startingPrice}</p>
                </div>
                <button
                  onClick={() => handleBookService('deep')}
                  className="px-4 py-2 sm:px-6 sm:py-3 bg-[#2E7D32] text-white rounded-lg text-sm sm:text-base font-semibold hover:bg-[#4CAF50] hover:shadow-lg hover:shadow-[#4CAF50]/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200">
                  Book Now
                </button>
              </div>
            </div>
          </div>
          
          {/* Standard Clean - Tall Card */}
          <div className="lg:row-span-2 bg-card border border-border rounded-xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 hover:border-[#4CAF50] transition-all duration-300 group reveal delay-100">
            <div className="relative h-48 sm:h-56 md:h-60 lg:h-80 overflow-hidden">
              <AppImage
                src={services[1].imageUrl}
                alt={services[1].imageAlt}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
                quality={80}
                className="object-cover group-hover:scale-105 transition-transform duration-500" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6">
                <div className="flex items-center gap-3 mb-2 sm:mb-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-full flex items-center justify-center">
                    <Icon name={services[1].iconName} size={20} variant="solid" className="text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white">{services[1].name}</h3>
                    <p className="text-xs sm:text-sm text-white/80">{services[1].duration}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 md:p-5 sm:p-6">
              <p className="text-muted-foreground mb-3 sm:mb-4 text-sm sm:text-base">{services[1].description}</p>
              <ul className="space-y-2 mb-4 sm:mb-6">
                {services[1].features.map((feature, idx) =>
                <li key={`${services[1].id}_feature_${idx}`} className="flex items-center gap-2 text-sm text-foreground">
                    <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-primary flex-shrink-0" />
                    {feature}
                  </li>
                )}
              </ul>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs sm:text-sm text-muted-foreground">Starting at</span>
                  <p className="text-2xl sm:text-3xl font-bold text-primary">{services[1].startingPrice}</p>
                </div>
                <button
                  onClick={() => handleBookService('standard')}
                  className="px-4 py-2 sm:px-6 sm:py-3 bg-[#2E7D32] text-white rounded-lg text-sm sm:text-base font-semibold hover:bg-[#4CAF50] hover:shadow-lg hover:shadow-[#4CAF50]/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200">
                  Book Now
                </button>
              </div>
            </div>
          </div>
          
          {/* Move-In/Out - Square Card */}
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 hover:border-[#4CAF50] transition-all duration-300 group reveal delay-200">
            <div className="relative h-40 sm:h-44 md:h-48 overflow-hidden">
              <AppImage
                src={services[2].imageUrl}
                alt={services[2].imageAlt}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
                quality={80}
                className="object-cover group-hover:scale-105 transition-transform duration-500" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-primary rounded-full flex items-center justify-center">
                    <Icon name={services[2].iconName} size={18} variant="solid" className="text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-white">{services[2].name}</h3>
                    <p className="text-xs text-white/80">{services[2].duration}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-3 md:p-4 sm:p-4">
              <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3">{services[2].description}</p>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs text-muted-foreground">Starting at</span>
                  <p className="text-xl sm:text-2xl font-bold text-primary">{services[2].startingPrice}</p>
                </div>
                <button
                  onClick={() => handleBookService('moveinout')}
                  className="px-3 py-1.5 sm:px-4 sm:py-2 bg-[#2E7D32] text-white rounded-lg text-xs sm:text-sm font-semibold hover:bg-[#4CAF50] hover:shadow-lg hover:shadow-[#4CAF50]/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200">
                  Book
                </button>
              </div>
            </div>
          </div>
          
          {/* Office Cleaning - Square Card */}
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 hover:border-[#4CAF50] transition-all duration-300 group reveal delay-300">
            <div className="relative h-40 sm:h-44 md:h-48 overflow-hidden">
              <AppImage
                src={services[3].imageUrl}
                alt={services[3].imageAlt}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
                quality={80}
                className="object-cover group-hover:scale-105 transition-transform duration-500" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-primary rounded-full flex items-center justify-center">
                    <Icon name={services[3].iconName} size={18} variant="solid" className="text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-white">{services[3].name}</h3>
                    <p className="text-xs text-white/80">{services[3].duration}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-3 md:p-4 sm:p-4">
              <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3">{services[3].description}</p>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs text-muted-foreground">Starting at</span>
                  <p className="text-xl sm:text-2xl font-bold text-primary">{services[3].startingPrice}</p>
                </div>
                <button
                  onClick={() => handleBookService('office')}
                  className="px-3 py-1.5 sm:px-4 sm:py-2 bg-[#2E7D32] text-white rounded-lg text-xs sm:text-sm font-semibold hover:bg-[#4CAF50] hover:shadow-lg hover:shadow-[#4CAF50]/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200">
                  Book
                </button>
              </div>
            </div>
          </div>
          
        </div>
        
        {/* Bottom CTA */}
        <div className="text-center mt-10 md:mt-12 lg:mt-12 reveal delay-400">
          <p className="text-muted-foreground mb-4">
            Need a custom cleaning plan? We offer flexible scheduling and packages.
          </p>
          <button
            onClick={handleContactUs}
            className="inline-flex items-center gap-2 text-primary font-semibold hover:underline transition-all active:scale-95">
            Contact Us for Custom Quote
            <Icon name="ArrowRightIcon" size={20} variant="outline" />
          </button>
        </div>
        
      </div>
    </section>);

}